<template>
  <div class="panel-container">
    <!-- Collective Stats Dashboard -->
    <div v-if="programStats" class="stats-dashboard mb-3">
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-icon">
            <meditation-svg></meditation-svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ programStats.meditation.total_time }}</div>
            <div class="stat-label">Meditation Time</div>
            <div class="stat-sub">{{ programStats.meditation.total_sessions }} sessions</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <contraction-svg></contraction-svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ programStats.contractions.total_logged }}</div>
            <div class="stat-label">Contractions Logged</div>
            <div class="stat-sub">Since {{ programStats.start_date || 'start' }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-check-double"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ programStats.contractions.total_processed }}</div>
            <div class="stat-label">Contractions Processed</div>
            <div class="stat-sub">{{ contractionProcessedPercent }}% completion</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-hands-helping"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ programStats.supported_others }}</div>
            <div class="stat-label">Supported others</div>
            <div class="stat-sub">Since {{ programStats.start_date || 'start' }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="members && members.length > 5" class="row mb-2">
      <div class="col-sm-5 col-12 my-1">
        <b-input-group size="sm">
          <b-form-input
            id="filter-input"
            v-model="filter"
            type="search"
            placeholder="Search Members"
          ></b-form-input>
          <b-input-group-append>
            <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
          </b-input-group-append>
        </b-input-group>
      </div>

      <div class="col-sm-3 col-5 my-1">
        <b-form-select
          id="per-page-select"
          v-model="perPage"
          :options="pageOptions"
          size="sm"
        ></b-form-select>
      </div>

      <div class="col-sm-4 col-2 my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          size="sm"
          class="my-0"
        ></b-pagination>
      </div>
    </div>

    <b-table
      id="connections_table"
      bordered
      responsive="md"
      :items="members"
      :busy="isBusy"
      :fields="fields"
      :filter="filter"
      :filter-included-fields="filterOn"
      @filtered="onFiltered"
      :current-page="currentPage"
      :per-page="perPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-compare="sortCompare"
      sort-icon-left
      class="small"
      show-empty
      empty-text="There are no members to show..."
    >
      <template v-slot:table-busy>
        <div class="text-center my-2 p-2">
          <b-spinner class="align-middle"></b-spinner>
          <br />
          <strong>Loading...</strong>
        </div>
      </template>

      <template v-slot:cell(full_name)="data">
        <div class="d-flex justify-content-between  align-items-center">

          <div class="d-flex align-items-center" style="gap: 8px">
            <div class="member-avatar-wrap"
                 v-b-tooltip.hover
                 :title="data.item.is_available ? 'Available' : 'Unavailable'"
            >
              <router-link :to="'/member/' + data.item.id + '?from=/programs/' + $route.params.challenge_id">
                <img
                    v-if="data.item.profile_image"
                    :src="data.item.profile_image"
                    class="member-avatar"
                    alt=""
                />
                <img
                    v-else
                    src="https://media.iamconnected.com/profile_images/default.png"
                    class="member-avatar"
                    alt=""
                />
              </router-link>
              <span
                  class="member-status-dot"
                  :class="data.item.is_available ? 'dot-available' : 'dot-unavailable'"
              ></span>
            </div>
            <router-link
                :to="'/member/' + data.item.id + '?from=/programs/' + $route.params.challenge_id"
                class="member-name-link"
                v-b-tooltip.hover
                title="View Profile"
            >
              <template v-if="data.item.full_name">
                {{ data.item.full_name }}
              </template>
              <i v-else>No name</i>
            </router-link>
          </div>

          <div>
            <button
                class="border-0 bg-transparent" style="color: #d6b674"
                v-b-tooltip.hover title="Request a process"
                @click="openRequestModal(data.item)"
            >
              <i class="fas fa-hands-helping"></i>
            </button>

            <router-link
                :to="{ path: `/chatbox/${data.item.id}`, query: { from: $route.fullPath } }"
                class="member-msg-icon"
                v-b-tooltip.hover
                title="Send Message"
            >
              <i class="fas fa-comment-dots"></i>
            </router-link>
          </div>
        </div>
      </template>

    </b-table>

    <div v-if="members && members.length > 5" class="my-2">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="right"
        size="sm"
        class="my-0"
      ></b-pagination>
    </div>

    <process-request-popup
        :requestTarget="requestTarget"
        v-model="showRequestModal"
        v-if="showRequestModal"
    ></process-request-popup>

  </div>

</template>
<script>
import ContractionSvg from "@/components/svg/ContractionSvg.vue";
import MeditationSvg from "@/components/svg/MeditationSvg.vue";
import axios from "@/axios";
import ProcessRequestPopup from "@/components/Processing/ProcessRequestPopup.vue";
export default {
  name: 'ProgramMember',
  components: {
    ProcessRequestPopup,
    MeditationSvg,
    ContractionSvg
  },
  props: {
    members: Array,
    isBusy: Boolean,
    filterOn: Array,
  },

  data() {
    return {
      currentPage: 1,
      totalRows: 0,
      perPage: 50,
      programStats: null,
      filter: null,
      pageOptions: [
        { value: 50, text: 'Num to Show' },
        25,
        50,
        100,
        { value: 500, text: 'All' },
      ],
      fields: [
        { key: 'full_name', label: 'Name', sortable: true },
      ],
      sortBy: 'full_name',
      sortDesc: false,

      requestTarget: null,
      showRequestModal: false
    }
  },
  methods: {
    openRequestModal(member) {
      this.requestTarget = member;
      this.showRequestModal = true;
    },

    fetchProgramStats() {
      if (!this.$route.params.challenge_id) return;

      axios(`programStats?challenge_id=${this.$route.params.challenge_id}`)
        .then((response) => {
          if (response.data.status === 1) {
            this.programStats = response.data;
          }
        })
        .catch((error) => {
          console.error('Error fetching program stats:', error);
        });
    },

    arrayElmGet(obj, value) {
      const entries = Object.entries(obj);
      for (const v of entries) {
        if (parseInt(v[1].value) === parseInt(value)) {
          return v[1].text;
        }
      }
      return null;
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    sortCompare(a, b, key, sortDesc) {
      if (key === 'full_name') {
        const nameA = a.full_name;
        const nameB = b.full_name;
        if (!nameA && !nameB) {
          return 0;
        }
        // keep empties at the end regardless of sort direction
        if (!nameA) {
          return sortDesc ? -1 : 1;
        }
        if (!nameB) {
          return sortDesc ? 1 : -1;
        }
        return nameA.localeCompare(nameB);
      }
      return false;
    },

  },

  computed: {
    contractionProcessedPercent() {
      if (!this.programStats || !this.programStats.contractions) return 0;
      const logged = this.programStats.contractions.total_logged;
      const processed = this.programStats.contractions.total_processed;
      if (logged === 0) return 0;
      return Math.round((processed / logged) * 100);
    },
  },

  mounted() {
    this.fetchProgramStats()
  },
}
</script>

<style scoped>
.panel-container {
  max-height: 750px;
  overflow-y: auto;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: #f8f9fa;
  flex: 1;
}

th.score_width {
  min-width: 180px !important;
}
th.last_session {
  min-width: 160px !important;
}
::v-deep .table thead th {
  vertical-align: middle !important;
}

::v-deep .b-dropdown .btn:hover .fa-angle-down.gold {
  color: #fff !important;
}

::v-deep .member-dropdown {
  display: inline-flex;
}

::v-deep .member-dropdown .btn {
  padding: 0.25rem 0.5rem;
  background-color: #6c757d;
  border-color: #6c757d;
  color: #fff;
}

::v-deep .member-dropdown .btn:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

::v-deep .member-dropdown .btn .gold {
  color: #d6b674;
}

::v-deep .member-dropdown .btn:hover .gold {
  color: #fff;
}

/* Collective Stats Dashboard */
.stats-dashboard {
  padding-bottom: 15px;
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
}

.stat-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border-radius: 8px;
  padding: 8px 11px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  min-width: 155px;
}

.stat-icon {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d6b674 0%, #c5a563 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon i {
  color: white;
  font-size: 1.1rem;
}

.stat-icon svg {
  width: 24px;
  height: 24px;
}

::v-deep .stat-icon svg {
  stroke: white;
}

::v-deep .stat-icon svg path {
  stroke: white;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #d6b674;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-sub {
  font-size: 0.6rem;
  color: #888;
}

@media (max-width: 768px) {
  .stats-row {
    flex-direction: column;
    gap: 10px;
  }

  .stat-card {
    padding: 10px 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
  }

  .stat-value {
    font-size: 1.2rem;
  }
}

/* Member Cell */
.member-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Avatar with status dot overlay */
.member-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.member-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #eee;
  transition: border-color 0.2s ease;
}

.member-avatar-wrap:hover .member-avatar {
  border-color: #d6b674;
}

/* Availability dot on top-right of avatar */
.member-status-dot {
  position: absolute;
  top: -1px;
  right: -1px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  cursor: default;
}

.member-status-dot.dot-available {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
}

.member-status-dot.dot-unavailable {
  background-color: #ccc;
}

/* Member name */
.member-name-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.member-name-link:hover {
  color: #d6b674;
  text-decoration: none;
}

/* Message icon */
.member-msg-icon {
  color: #d6b674;
  font-size: 1rem;
  transition: all 0.2s ease;
  text-decoration: none;
  margin-left: auto;
}

.member-msg-icon:hover {
  color: #c4a55a;
  transform: scale(1.15);
  text-decoration: none;
}
</style>
